import {
  addToaster,
  DataTypeStore,
  hideLoader,
  reloadEligibilityAssessment,
  showLoader,
  WORKFLOW_UI_IDENTIFIER,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  TableColumnType,
  Accordion,
  CustomTable,
  SkeletonLoader,
  SvgReload,
  FormInput,
} from '@ui-kit'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  editEntityDataEmbeddedAPI,
  getCaseKeyCaseNameByEntity,
} from '../../apis/entityData.api'
import { PHARMACY_TRIAGE_STRINGS } from '../../utils/strings'
import {
  PAP_WORKFLOW_CONSTANTS,
  PHARMACY_TRIAGE_CONSTANTS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { EligibilityAssessmentDataType } from '../../types/types'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'

export const PharmacyTriageEligibilityAssessment = ({
  task,
  uiIdentifierRef,
}: {
  task?: WorkflowInstanceTaskType
  uiIdentifierRef: React.MutableRefObject<{
    [key: string]: {
      updateCaseData?: () => Promise<boolean>
    }
  }>
}) => {
  const { caseId } = useParams()

  const [eligibilityAssessmentData, setEligibilityAssessmentData] = useState<
    DataTypeStore[]
  >([])
  const [showDenialReason, setShowDenialReason] = useState<boolean>(false)
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const schemaName = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant?.schema_name
  )
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState<boolean>(false)

  const formState: EligibilityAssessmentDataType = {}

  const { setValue, register, watch } = useForm({ defaultValues: formState })

  const columns: TableColumnType<DataTypeStore>[] = [
    {
      header: PHARMACY_TRIAGE_CONSTANTS.ELIGIBILITY_CRITERIA_LABEL,
      accessor: PHARMACY_TRIAGE_CONSTANTS.ELIGIBILITY_CRITERIA,
      dataType: 'string',
    },
    {
      header: PHARMACY_TRIAGE_CONSTANTS.INPUT_LABEL,
      accessor: PHARMACY_TRIAGE_CONSTANTS.INPUT,
      type: 'component',
      renderComponent: (row: DataTypeStore) => (
        <div className="font-light">
          {Array.isArray(row?.input)
            ? row?.input?.map((item: string | number, index: number) => (
                <div key={`${item}_${index}`}>{item}</div>
              ))
            : ''}
        </div>
      ),
    },
    {
      header: PHARMACY_TRIAGE_CONSTANTS.OUTCOME_LABEL,
      accessor: PHARMACY_TRIAGE_CONSTANTS.OUTCOME,
    },
    {
      header: PHARMACY_TRIAGE_CONSTANTS.OUTCOME_REASON_LABEL,
      accessor: PHARMACY_TRIAGE_CONSTANTS.OUTCOME_REASON,
    },
  ]

  const layout: {
    name:
      | 'enrollment_type'
      | 'enrollment_subtype'
      | 'enrollment_reason'
      | 'decision'
      | 'decision_reason'
      | 'denial_reason'
      | 'request_for_exception'
      | 'request_for_exception_reason'
    label: string
    type?: string
    row?: number
    colSpan?: number
    required?: boolean
    visible?: boolean
    isEditable?: boolean
  }[] = [
    ...(task?.identifier?.ui_identifier ===
    WORKFLOW_UI_IDENTIFIER.C_PAP_ACCESS_ELIGIBILITY
      ? [
          {
            name: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_TYPE_KEY,
            label: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_TYPE_LABEL,
            required: true,
          },
          {
            name: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_SUBTYPE_KEY,
            label: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_SUBTYPE_LABEL,
            required: true,
          },
        ]
      : []),
    {
      name: PAP_WORKFLOW_CONSTANTS.DECISION_KEY,
      label: PAP_WORKFLOW_CONSTANTS.DECISION_LABEL,
      required: true,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS.DECISION_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS.DECISION_REASON_LABEL,
      required: true,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS.DENIAL_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS.DENIAL_REASON_LABEL,
      visible: showDenialReason,
    },
  ]

  const fetchOutcomeDetails = async () => {
    try {
      setLoading(true)
      const payload = {
        entity_name: 'pap_eligibility_assessments',
        select: [
          'id',
          'case',
          'c_pap_eligibility_assessment_details_eligibility_assessment',
          'pap_enrollment_sub_type',
          'pap_enrollment_type',
          'status',
          'decision',
          'decision_reason',
          'request_for_exception',
          'request_for_exception_reason',
        ],
        related_entities: {
          c_pap_eligibility_assessment_details_eligibility_assessment: {
            select: [
              'id',
              'eligibility_config',
              'output_json',
              'eligibility_outcome',
              'eligibility_outcome_reason',
            ],
            related_entities: {
              eligibility_config: {
                select: ['id', 'eligibility_rule'],
                related_entities: {
                  eligibility_rule: {
                    select: ['id', 'rule_label'],
                  },
                },
              },
            },
          },
        },
        eligibility_outcome: {
          select: ['id', 'name'],
        },
        eligibility_outcome_reason: {
          select: ['id', 'name'],
        },
      }
      const response = await getCaseKeyCaseNameByEntity(
        payload,
        'pap_eligibility_assessments',
        (Number(task?.resource_id) ?? 0).toString()
      )

      const denialReasonConfig =
        (
          response?.data
            ?.c_pap_eligibility_assessment_details_eligibility_assessment ?? []
        )
          ?.filter(
            (item: { eligibility_outcome: { name: string } }) =>
              item.eligibility_outcome.name ===
                PAP_WORKFLOW_CONSTANTS.NOT_ELIGIBLE ||
              item.eligibility_outcome.name ===
                PAP_WORKFLOW_CONSTANTS.UNDETERMINED
          )
          ?.map(
            (item: {
              eligibility_config: {
                eligibility_rule: { rule_label: string }
              }
            }) => item.eligibility_config.eligibility_rule.rule_label
          )
          ?.join(', ') ?? ''

      setValue('id', response.data?.id?.toString() ?? '')
      setValue('decision', response.data?.decision?.name ?? '')
      if (response.data?.decision?.name === PAP_WORKFLOW_CONSTANTS.DENIED) {
        setShowDenialReason(true)
      }
      setValue('decision_reason', response.data?.decision_reason?.name ?? '')
      setValue('denial_reason', denialReasonConfig)
      setValue(
        'enrollment_type',
        response.data?.pap_enrollment_type?.name ?? ''
      )
      setValue(
        'enrollment_subtype',
        response.data?.pap_enrollment_sub_type?.name ?? ''
      )

      const data: DataTypeStore[] = []
      response.data?.c_pap_eligibility_assessment_details_eligibility_assessment.forEach(
        (item: {
          eligibility_outcome: { name: string }
          eligibility_outcome_reason: { name: string }
          eligibility_config: {
            eligibility_rule: { rule_label: string }
          }
          status: string
          output_json: {
            label: string
            value: string
            diagnosis_code_type: string
          }[]
        }) => {
          data.push({
            criteria: item?.eligibility_config?.eligibility_rule?.rule_label,
            outcome: item?.eligibility_outcome?.name,
            outcome_reason: item?.eligibility_outcome_reason?.name,
            input: Array.isArray(item?.output_json)
              ? item?.output_json?.map(
                  (dataItem: {
                    label: string
                    value: string
                    diagnosis_code_type: string
                  }) => {
                    if (dataItem.label)
                      return dataItem.label + ': ' + dataItem.value
                    else return dataItem.value
                  }
                )
              : [],
          })
        }
      )
      setEligibilityAssessmentData(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const updateCaseDetails = async () => {
    if (watch('request_for_exception')) {
      try {
        const data = {
          request_for_exception: watch('request_for_exception'),
          request_for_exception_reason: watch('request_for_exception_reason'),
          decision_id: 3,
          decision_reason_id: 5,
        }
        await editEntityDataEmbeddedAPI(
          task?.identifier?.entity_name ?? '',
          watch('id') ?? '',
          data,
          [
            'request_for_exception',
            'request_for_exception_reason',
            'decision_id',
            'decision_reason_id',
          ]
        )
        const caseData = {
          case_status_id:
            task?.identifier?.pap_review_exception_status_id ?? '',
        }
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entity_name ?? '',
          workflowInstance?.case_id ?? caseId ?? '',
          caseData,
          ['users', 'case_status_id']
        )
        dispatch(
          addToaster({
            type: 'success',
            message: TOASTER_NAMESPACE.CASE_MOVED_TO_EXCEPTION_STATUS,
            id: uuid(),
          })
        )
        return true
      } catch (err) {
        console.error(err)
        return false
      }
    } else return true
  }

  const handleReloadButtonClick = async () => {
    try {
      dispatch(showLoader())
      await reloadEligibilityAssessment(
        caseId ?? '',
        task?.identifier?.ui_identifier ===
          WORKFLOW_UI_IDENTIFIER.C_PAP_ACCESS_ELIGIBILITY,
        schemaName ?? ''
      )
      await fetchOutcomeDetails()
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (task?.resource_id) fetchOutcomeDetails()
    uiIdentifierRef.current = {
      ...uiIdentifierRef.current,
      [task?.id ?? '']: {
        updateCaseData: updateCaseDetails,
      },
    }
  }, [])

  if (loading) {
    return <SkeletonLoader />
  }

  if (task?.resource_id) {
    return (
      <form>
        <div className="border rounded-b p-3 border-accent-100 flex flex-col">
          <Accordion
            alwaysOpen={true}
            heading={
              <div className="flex justify-between">
                <span>Results</span>
                {task?.state === WORKFLOW_STATUS.OPEN ? (
                  <span
                    className="cursor-pointer flex p-1"
                    onClick={handleReloadButtonClick}
                  >
                    <SvgReload />
                  </span>
                ) : null}
              </div>
            }
          >
            <div className="p-3">
              <div className="grid grid-cols-3 gap-5">
                {layout
                  .filter((input) => input.visible !== false)
                  .map((input) => {
                    if (input.isEditable) {
                      return (
                        <div
                          className="w-full min-w-[11.69rem]"
                          key={input.name}
                          style={{
                            gridRowStart: input.row ?? undefined,
                            gridColumn: input.colSpan
                              ? `span ${input.colSpan}`
                              : '',
                          }}
                        >
                          <FormInput
                            dataQaId={`requestor-search-${input.name}`}
                            label={input.label}
                            name={input.name}
                            type={'text'}
                            tabIndex={0}
                            register={register}
                            placeholder="--"
                          />
                        </div>
                      )
                    }
                    return (
                      <div
                        className="w-full min-w-[11.69rem]"
                        key={input.name}
                        style={{
                          gridRowStart: input.row ?? undefined,
                          gridColumn: input.colSpan
                            ? `span ${input.colSpan}`
                            : '',
                        }}
                      >
                        <div className="font-normal break-words text-sm">
                          {input.label}
                          {input?.required ? (
                            <span
                              className={`ml-1 text-xs leading-1 text-error-500`}
                            >
                              *
                            </span>
                          ) : null}
                        </div>
                        <div className="font-extralight text-xs min-h-4.5">
                          {watch(input.name) ?? '--'}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </Accordion>
          <Accordion
            heading={
              <div>
                {task.identifier?.ui_identifier ===
                WORKFLOW_UI_IDENTIFIER.C_PAP_ACCESS_ELIGIBILITY
                  ? PHARMACY_TRIAGE_CONSTANTS.OUTCOME_LABEL
                  : PHARMACY_TRIAGE_STRINGS.ELIGIBILITY_ASSESSMENT}
              </div>
            }
            alwaysOpen={true}
          >
            <div className="p-2">
              <CustomTable
                columns={columns}
                data={eligibilityAssessmentData}
                emptyDataMessage="No Data"
                showTableColumnOnEmpty={true}
              />
            </div>
          </Accordion>
        </div>
      </form>
    )
  } else return null
}
